import { useState, useEffect, useContext } from 'react';
import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { InvoiceDetails } from '../types';
import {
  InvoiceFormValues,
  InvoiceFormProduct,
  initialInvoiceValues
} from '../Constants/invoiceFormikData';
import invoiceApi from '../helpers/invoiceApi';

export type InvoiceFormMode = 'create' | 'edit' | 'duplicate';

const mapDetailsToForm = (
  data: InvoiceDetails,
  mode: InvoiceFormMode
): InvoiceFormValues => {
  const isDuplicate = mode === 'duplicate';
  const { invoiceDateEpoch } = data;
  const { invoiceDueDateEpoch } = data;

  const invoiceDate =
    invoiceDateEpoch && invoiceDateEpoch > 0
      ? new Date(invoiceDateEpoch)
      : null;

  let invoiceDueDate: Date | null = null;
  if (invoiceDueDateEpoch && invoiceDueDateEpoch > 0) {
    const d = new Date(invoiceDueDateEpoch);
    if (d.toISOString() !== '1970-01-01T00:00:00.000Z') {
      invoiceDueDate = d;
    }
  }

  const UNASSIGNED_SEASON_ID = '88888888-8888-8888-8888-888888888888';
  const cropSeason = (data.cropSeasons ?? [])
    .map(cs => cs.seasonId)
    .filter(id => id !== UNASSIGNED_SEASON_ID);

  const invoiceProducts: InvoiceFormProduct[] = (
    data.invoiceProducts ?? []
  ).map(p => ({
    lineId: crypto.randomUUID(),
    ...(isDuplicate ? {} : { id: p.id, trackingId: p.trackingId }),
    productId: p.productId ?? '',
    productName: p.productName ?? '',
    custom: p.custom ?? false,
    totalQuantityValue: p.totalQuantityValue ?? 0,
    totalQuantityUnit: p.totalQuantityUnit ?? '',
    totalCostValue: p.totalCostValue ?? 0,
    // Carry the catalog density the invoice service needs for unit math
    // (FR-095) so an edit/duplicate save doesn't 500 even when the user never
    // re-opens each product's drawer.
    productDensity: p.productDensity ?? undefined,
    // A line saved with its rate reopens showing exactly what was entered.
    // Absent on older lines, which leaves the drawer and the tables to recover
    // the rate by dividing, as they did before it was stored.
    priceValue: p.unitPriceValue ?? undefined,
    priceUnit: p.unitPriceUnit ?? undefined
  }));

  return {
    invoiceNumber: isDuplicate ? '' : data.invoiceNumber ?? '',
    vendor: data.vendor ?? '',
    invoiceDate,
    invoiceDueDate,
    cropSeason,
    tagIds: data.flagIds ?? [],
    notes: data.notes ?? '',
    invoiceProducts
  };
};

export interface UseInvoiceFormByIdReturn {
  formValues: InvoiceFormValues;
  isLoading: boolean;
  error: string | null;
}

const useInvoiceFormById = (
  id: string | null,
  mode: InvoiceFormMode
): UseInvoiceFormByIdReturn => {
  const [, dispatch] = useContext(Context) as any;
  const [formValues, setFormValues] = useState<InvoiceFormValues>(
    initialInvoiceValues
  );
  const [isLoading, setIsLoading] = useState(!!id && mode !== 'create');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || mode === 'create') {
      setIsLoading(false);
      return () => {};
    }
    setIsLoading(true);
    const { promise, cancel } = invoiceApi.fetch(id);
    promise
      .then(({ data }) => {
        setFormValues(mapDetailsToForm(data, mode));
      })
      .catch(catchCancel)
      .catch((err: any) => {
        setError(err?.message || 'Failed to load invoice');
        parseServerError(dispatch)(err);
      })
      .finally(() => setIsLoading(false));
    return () => cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  return { formValues, isLoading, error };
};

export default useInvoiceFormById;
